import React from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton, SkeletonItem} from 'src/components/skeleton';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import {ConnectorLimitsHeader} from './connector-limits-header';
import {ConnectorLimitsList} from './connector-limits-list';
import {TemporaryLimitControls} from './temporary-limit-controls';

import type {OnChangeLimits, TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Limit} from 'src/openapi/admin/models/Limit';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    busy: boolean,
    editDisabled: boolean,
    isPremiumMcpEnabled: boolean,
    mcpLimitsDictionary: DictionaryItem[],
    data: Limit[],
    tempLimitsSettings: TempLimitsSettings,
    anyChanged: boolean,
    isLoadingAny: boolean,
    combinedError: Error | null,
    today: Date,
    isDefaultLimits: boolean,

    getDefaultValue: (code: string) => number,
    getUsedValue?: (code: string) => number | undefined,

    saveAction?: React.ReactNode,
    onSave: () => void,
    onDiscard: () => void,
    onLimitChange: OnChangeLimits,
    onResetToDefaults: () => void,
    changeTemporaryLimit: OnChangeLimits,
    clearTemporaryLimit: (limitId: string) => void,
    clearCustomLimit: (limitId: string) => void,
    changeTemporaryLimitMode: (
        changeData: CheckBoxChangeData,
    ) => void,
    onExpiresOnChange: (expiresOn: string) => void,

    tooltipText: string,
    dateInputId: string,
};

export const ConnectorLimitsForm = ({
    busy,
    editDisabled,
    isPremiumMcpEnabled,
    mcpLimitsDictionary,
    data,
    tempLimitsSettings,
    anyChanged,
    isLoadingAny,
    combinedError,
    today,
    isDefaultLimits,
    getDefaultValue,
    getUsedValue,
    saveAction,
    onSave,
    onDiscard,
    onLimitChange,
    onResetToDefaults,
    changeTemporaryLimit,
    clearTemporaryLimit,
    clearCustomLimit,
    changeTemporaryLimitMode,
    onExpiresOnChange,
    tooltipText,
    dateInputId,
}: Props) => (
    <ProfileMain header={(
        <ConnectorLimitsHeader
            busy={busy}
            editDisabled={editDisabled}
            anyChanged={anyChanged}
            isLoadingAny={isLoadingAny}
            saveAction={saveAction}
            onSave={onSave}
            onDiscard={onDiscard}
        />
    )}>
        <ServerResponseHandler
            serverError={combinedError}
            sectionName="Connector Limits">
            <Skeleton
                shown={isLoadingAny}
                view={(
                    <SkeletonItem
                        width={300}
                        height={56}
                    />
                )}>
                <FormSection>
                    <Group direction="v">
                        <Group>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={
                                    editDisabled
                                    || isDefaultLimits
                                }
                                onClick={
                                    onResetToDefaults
                                }>
                                Use default limits
                            </Button>
                            {isDefaultLimits ? (
                                <div>
                                    Currently using
                                    default limits.
                                </div>
                            ) : (
                                <div>
                                    <b>
                                        Currently using
                                        custom limits.
                                    </b>{' '}
                                    Reset to defaults.
                                </div>
                            )}
                        </Group>
                        <TemporaryLimitControls
                            editDisabled={editDisabled}
                            expiresOn={
                                tempLimitsSettings
                                    .expiresOn
                            }
                            dateInputId={dateInputId}
                            today={today}
                            changeTemporaryLimitMode={
                                changeTemporaryLimitMode
                            }
                            onExpiresOnChange={
                                onExpiresOnChange
                            }
                        />
                        <ConnectorLimitsList
                            editDisabled={editDisabled}
                            isPremiumMcpEnabled={
                                isPremiumMcpEnabled
                            }
                            mcpLimitsDictionary={
                                mcpLimitsDictionary
                            }
                            data={data}
                            tempLimitsSettings={
                                tempLimitsSettings
                            }
                            tooltipText={tooltipText}
                            getDefaultValue={
                                getDefaultValue
                            }
                            getUsedValue={getUsedValue}
                            onLimitChange={onLimitChange}
                            changeTemporaryLimit={
                                changeTemporaryLimit
                            }
                            clearTemporaryLimit={
                                clearTemporaryLimit
                            }
                            clearCustomLimit={
                                clearCustomLimit
                            }
                        />
                    </Group>
                </FormSection>
            </Skeleton>
        </ServerResponseHandler>
    </ProfileMain>
);
